import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useLocation } from "react-router-dom";
import { Gamepad2, X, Maximize2, Minimize2 } from "lucide-react";
import { apiRequest } from "../utils/api";
import trapAndGooImg from "../assets/trap-and-goo.webp";
import forestRushImg from "../assets/forest-rush.webp";
import draugutsImg from "../assets/drauguts.jpg";
import frontlineImg from "../assets/frontline.webp";
import cyrusBeanJumpImg from "../assets/cyrus-bean-jump.webp";
import dandoRushImg from "../assets/dando-rush.webp";
import draughtsImg from "../assets/draughts.webp";
import gunWarsImg from "../assets/gun-wars.webp";
import omiImg from "../assets/omi.webp";
import poolLeagueImg from "../assets/pool-league.webp";
import carromLeagueImg from "../assets/carrom-league.webp";

// Map API thumbnailUrl filenames to local imported assets
const THUMBNAIL_MAP = {
  "trap-and-goo.jpg": trapAndGooImg,
  "trap-and-goo.webp": trapAndGooImg,
  "forest-rush.jpg": forestRushImg,
  "forest-rush.webp": forestRushImg,
  "drauguts.jpg": draugutsImg,
  "drauguts.webp": draugutsImg,
  "draughts.jpg": draughtsImg,
  "draughts.webp": draughtsImg,
  "frontline.jpg": frontlineImg,
  "frontline.webp": frontlineImg,
  "cyrus-bean-jump.webp": cyrusBeanJumpImg,
  "dando-rush.webp": dandoRushImg,
  "gun-wars.webp": gunWarsImg,
  "omi.webp": omiImg,
  "Trap and Goo": trapAndGooImg,
  "Forest Rush": forestRushImg,
  "Draughts": draughtsImg,
  "Frontline": frontlineImg,
  "Cyrus Bean Jump": cyrusBeanJumpImg,
  "Dando Rush": dandoRushImg,
  "Gun Wars": gunWarsImg,
  "Omi": omiImg,
  "pool-league.webp": poolLeagueImg,
  "carrom-league.webp": carromLeagueImg,
  "Pool League": poolLeagueImg,
  "Carrom League": carromLeagueImg,
};

const GENRES = ["All", "Action", "Strategy", "Puzzle", "Racing", "Card"];

const Library = () => {
  const [selectedGenres, setSelectedGenres] = useState(["All"]);
  const [activeGame, setActiveGame] = useState(null);
  const [games, setGames] = useState([]);
  const [gamesLoading, setGamesLoading] = useState(true);
  const [gamesError, setGamesError] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  // If a game was passed via search navigation, launch it
  useEffect(() => {
    if (location.state?.selectedGame) {
      handleLaunchGame(location.state.selectedGame);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  // Fetch games from API whenever genre selection changes
  useEffect(() => {
    const fetchGames = async () => {
      try {
        setGamesLoading(true);
        setGamesError(null);

        // Build query string — no param means "All"
        let endpoint = "/Games/library";
        if (!selectedGenres.includes("All") && selectedGenres.length > 0) {
          const params = selectedGenres.map((g) => `genres=${encodeURIComponent(g)}`).join("&");
          endpoint = `/Games/library?${params}`;
        }

        const response = await apiRequest(endpoint);
        if (!response.ok) throw new Error("Failed to load games");
        const data = await response.json();

        const mapped = data.map((game) => ({
          id: game.id,
          title: game.title,
          category: game.genre,
          buildPath: game.launchUrl || "",
          multiplayerType: game.multiplayerType || "",
          image: (game.thumbnailUrl && THUMBNAIL_MAP[game.thumbnailUrl]) || THUMBNAIL_MAP[game.title] || null,
        }));

        setGames(mapped);
      } catch (err) {
        setGamesError("Could not load games. Please try again later.");
      } finally {
        setGamesLoading(false);
      }
    };

    fetchGames();
  }, [selectedGenres]);

  // --- AUTH CHECK LOGIC ---
  const handleLaunchGame = (game) => {
    const token = localStorage.getItem("accessToken");
    if (!token) {
      navigate("/login");
    } else if (!game.buildPath) {
      return; // Game not yet available
    } else {
      setActiveGame(game);
    }
  };

  const handleGenreChange = (genre) => {
    if (genre === "All") {
      setSelectedGenres(["All"]);
    } else {
      let updated = selectedGenres.filter((g) => g !== "All");
      if (updated.includes(genre)) {
        updated = updated.filter((g) => g !== genre);
        if (updated.length === 0) updated = ["All"];
      } else {
        updated.push(genre);
      }
      setSelectedGenres(updated);
    }
  };

  const [isFullscreen, setIsFullscreen] = useState(false);
  const gameContainerRef = useRef(null);

  // Manage body scroll lock when gameplay is active
  useEffect(() => {
    if (activeGame) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [activeGame]);

  // Fullscreen change listener
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      const elem = gameContainerRef.current || document.documentElement;
      if (elem.requestFullscreen) {
        elem.requestFullscreen().catch(() => {});
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen().catch(() => {});
      }
    }
  };

  const handleCloseGame = () => {
    if (document.fullscreenElement && document.exitFullscreen) {
      document.exitFullscreen().catch(() => {});
    }
    setActiveGame(null);
  };

  // --- GAMEPLAY VIEW ---
  if (activeGame) {
    return createPortal(
      <div 
        ref={gameContainerRef}
        className="fixed inset-0 w-screen h-screen bg-black text-white flex flex-col z-[99999] overflow-hidden"
        style={{ top: 0, left: 0, width: "100vw", height: "100vh", margin: 0, padding: 0 }}
      >
        {/* Controls Overlay: Fullscreen Toggle & Close Button */}
        <div className="absolute top-4 right-4 z-[100000] flex items-center gap-3">
          <button
            onClick={toggleFullscreen}
            className="flex items-center gap-2 px-4 py-2 bg-black/85 hover:bg-black border border-white/20 hover:border-[#FF7A00] text-white font-heading font-black uppercase tracking-wider text-xs rounded-xl shadow-lg transition-all duration-300 hover:shadow-[0_0_15px_rgba(255,122,0,0.5)] cursor-pointer group"
            title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
          >
            {isFullscreen ? (
              <>
                <Minimize2 size={16} className="text-gray-400 group-hover:text-[#FF7A00] transition-colors" />
                <span className="hidden sm:inline">Exit Fullscreen</span>
              </>
            ) : (
              <>
                <Maximize2 size={16} className="text-gray-400 group-hover:text-[#FF7A00] transition-colors" />
                <span className="hidden sm:inline">Fullscreen</span>
              </>
            )}
          </button>

          <button
            onClick={handleCloseGame}
            className="flex items-center gap-2 px-4 py-2 bg-black/85 hover:bg-black border border-white/20 hover:border-[#FF7A00] text-white font-heading font-black uppercase tracking-wider text-xs rounded-xl shadow-lg transition-all duration-300 hover:shadow-[0_0_15px_rgba(255,122,0,0.5)] cursor-pointer group"
          >
            <X size={16} className="text-gray-400 group-hover:text-[#FF7A00] transition-colors" />
            <span>Close Game</span>
          </button>
        </div>

        {/* Central Full Screen WebGL Game Canvas */}
        <div className="w-full h-full relative flex items-center justify-center bg-black">
          <iframe
            src={
              activeGame.buildPath.includes("?")
                ? `${activeGame.buildPath}&token=${encodeURIComponent(localStorage.getItem("accessToken") || "")}`
                : `${activeGame.buildPath}?token=${encodeURIComponent(localStorage.getItem("accessToken") || "")}`
            }
            className="w-full h-full border-none shadow-2xl"
            title={activeGame.title}
            allow="autoplay; fullscreen; keyboard; gamepad"
          />
        </div>
      </div>,
      document.body
    );
  }

  // --- LIBRARY VIEW ---
  return (
    <div className="flex flex-col md:flex-row gap-4 md:gap-8 p-4 md:p-6 animate-in fade-in duration-500">
      <aside className="w-full md:w-64 shrink-0 glass-panel p-6 rounded-2xl h-fit space-y-8">
        <section>
          <h3 className="font-heading font-black mb-4 border-b border-game-border pb-2 tracking-tighter uppercase text-game-accent">
            Genre
          </h3>
          {GENRES.map((g) => (
            <label key={g} className="flex items-center gap-3 mb-3 cursor-pointer group">
              <input
                type="checkbox"
                checked={selectedGenres.includes(g)}
                onChange={() => handleGenreChange(g)}
                className="w-4 h-4 accent-game-neon rounded cursor-pointer border-game-border bg-game-dark"
              />
              <span
                className={`text-xs uppercase tracking-tight transition-all duration-300 ${
                  selectedGenres.includes(g)
                    ? "font-black text-white drop-shadow-[0_0_5px_rgba(255,255,255,0.5)]"
                    : "text-game-muted group-hover:text-white"
                }`}
              >
                {g}
              </span>
            </label>
          ))}
        </section>
      </aside>

      <div className="flex-1">
        <div className="flex justify-between items-center mb-8 border-b border-white/5 pb-4">
          <h2 className="text-3xl font-heading font-black italic tracking-tighter uppercase text-transparent bg-clip-text bg-gradient-to-r from-game-text to-game-muted">
            Game Library
          </h2>
          <span className="text-[10px] font-black text-game-neon uppercase tracking-widest bg-game-neon/10 px-4 py-1.5 rounded-full border border-game-neon/20 shadow-[0_0_15px_rgba(255,184,0,0.2)]">
            {gamesLoading ? "Loading..." : `${games.length} titles available`}
          </span>
        </div>

        {gamesLoading ? (
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="p-2.5 sm:p-4 rounded-xl sm:rounded-2xl border border-game-border animate-pulse">
                <div className="w-full aspect-video bg-game-dark/60 rounded-lg sm:rounded-xl mb-3 sm:mb-4"></div>
                <div className="h-3 sm:h-4 bg-game-dark/60 rounded w-3/4 mb-2"></div>
                <div className="h-2 sm:h-3 bg-game-dark/40 rounded w-1/2 mb-3 sm:mb-4"></div>
                <div className="h-7 sm:h-10 bg-game-dark/60 rounded-lg sm:rounded-xl"></div>
              </div>
            ))}
          </div>
        ) : gamesError ? (
          <div className="text-center py-20 text-game-muted">
            <p className="text-sm font-bold uppercase tracking-widest">{gamesError}</p>
          </div>
        ) : games.length === 0 ? (
          <div className="text-center py-20 text-game-muted">
            <Gamepad2 size={48} className="mx-auto mb-4 opacity-20" />
            <p className="text-sm font-bold uppercase tracking-widest">No games found for selected genre</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6">
            {games.map((game) => (
              <div
                key={game.id}
                className="game-card-hover p-2.5 sm:p-4 rounded-xl sm:rounded-2xl flex flex-col items-center group relative overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-b from-transparent to-game-dark/80 z-0 opacity-50 group-hover:opacity-100 transition-opacity"></div>

                <div className="w-full aspect-video bg-game-dark flex items-center justify-center rounded-lg sm:rounded-xl mb-3 sm:mb-4 relative overflow-hidden z-10 border border-game-border group-hover:border-game-accent/50 transition-colors shadow-inner">
                  {game.image ? (
                    <img src={game.image} alt={game.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                  ) : (
                    <Gamepad2 size={32} className="text-game-border group-hover:text-game-accent transition-colors duration-500 group-hover:scale-110 sm:w-10 sm:h-10" />
                  )}
                </div>

                <div className="relative z-10 w-full flex-1 flex flex-col justify-between">
                  <div>
                    <h4 className="font-heading font-black text-white uppercase tracking-tighter italic text-sm sm:text-xl group-hover:text-game-neon transition-colors truncate">
                      {game.title}
                    </h4>
                    <div className="flex justify-between items-center text-[9px] sm:text-[10px] text-game-muted mt-1.5 sm:mt-2 uppercase font-black tracking-widest">
                      <span className="text-game-accent truncate max-w-[50px] sm:max-w-none">{game.category}</span>
                      {game.multiplayerType && (
                        <span className="bg-game-neon/10 border border-game-neon/30 text-game-neon px-1.5 py-0.5 rounded text-[7px] sm:text-[8px] shrink-0">
                          {game.multiplayerType}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <button
                  onClick={() => navigate(`/game/${game.id}`)}
                  className="game-btn w-full py-1.5 sm:py-3 text-xs sm:text-base rounded-lg sm:rounded-xl mt-3 sm:mt-5 z-10 relative opacity-90 group-hover:opacity-100 transition-all duration-300"
                >
                  Launch Game
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Library;
